/**
 * Helpers for TargetingV2 (wrapper target:insights / target:comparison).
 */

import {
  clearAllSelections,
  injectSearchIntoFilters,
  injectSpCampaignTypeFilter,
  isCampaignTypeSP,
  prepareWrapperFilters,
  buildWrapperInsightsQueryBody,
  SB_SD_OPTIMISATION_MODAL_NOTE,
  SB_SD_OPTIMISATION_TOOLTIP,
} from "../../helper/insights/insightsFilterUtils";
import modifyValueByPercentage, {
  isValidCpcForCopy,
} from "../../helper/common/modifyValueByPercentage";
import {
  TARGETING_V2_ALWAYS_MEASURES,
  TARGETING_V2_DEFAULT_DIMENSIONS,
  TARGETING_V2_PAGE_MEASURES,
} from "./targetingV2Configs";
import {
  getTargetingMinBid,
  getAmazonCurrencyCodeFromClientData,
  roundTargetingBid,
} from "./targetingV2BidConfig";

export {
  clearAllSelections,
  injectSearchIntoFilters,
  injectSpCampaignTypeFilter,
  isCampaignTypeSP,
  prepareWrapperFilters,
  SB_SD_OPTIMISATION_MODAL_NOTE,
  SB_SD_OPTIMISATION_TOOLTIP,
};

export function normalizeTargetingInsightRow(row) {
  const bid =
    row?.bid ??
    row?.current_data?.bid ??
    row?.targeting_expression?.target?.[0]?.bid ??
    "";
  return {
    ...row,
    bid,
    bid_copy: bid,
    status: row?.status ?? row?.campaign_status ?? "",
    isChecked: false,
    isEditable: false,
  };
}

export function normalizeTargetingInsightRows(rows) {
  return (rows || []).map((row) => normalizeTargetingInsightRow(row));
}

export function getTargetingSearchMembers(queryDimensions = []) {
  const dims = [...new Set(queryDimensions || TARGETING_V2_DEFAULT_DIMENSIONS)];
  return [
    ...new Set([
      ...dims,
      "portfolio_name",
      "ad_group_name",
      "campaign_name",
      "product_set",
      "targeting",
    ]),
  ];
}

export function buildTargetingInsightsQueryBody({
  queryDimensions = TARGETING_V2_DEFAULT_DIMENSIONS,
  filters = {},
  queryMeasure = TARGETING_V2_PAGE_MEASURES,
  queryOrder = [["spends", "desc"]],
  timeDimensions = [],
  offset = 0,
  limit = 50,
  extraQueryFields = {},
}) {
  return buildWrapperInsightsQueryBody({
    queryDimensions,
    filters,
    queryMeasure,
    alwaysMeasures: TARGETING_V2_ALWAYS_MEASURES,
    queryOrder,
    timeDimensions,
    offset,
    limit,
    extraQueryFields,
  });
}

export function toggleSingleSelection(rows, index) {
  return (rows || []).map((row, i) =>
    i === index ? { ...row, isChecked: !row.isChecked } : row
  );
}

export function toggleSelectAll(rows, checked) {
  return (rows || []).map((row) => ({ ...row, isChecked: checked }));
}

function ensureTargetExpression(row) {
  const expr = row?.targeting_expression
    ? JSON.parse(JSON.stringify(row.targeting_expression))
    : { target: [{}] };
  if (!Array.isArray(expr.target) || !expr.target[0]) {
    expr.target = [{}];
  }
  return expr;
}

export function buildStatusUpdatePayload(rows, status) {
  return (rows || []).map((row) => {
    const expr = ensureTargetExpression(row);
    expr.target[0].status = status;
    return expr;
  });
}

export function buildSingleBidUpdatePayload(row, bidValue) {
  const expr = ensureTargetExpression(row);
  expr.target[0].bid = Number(bidValue);
  if (row?.status) {
    expr.target[0].status = String(row.status).toUpperCase();
  }
  return [expr];
}

export function buildBulkBidUpdatePayload(selectedRows, adjustBidInfo) {
  const { selectedAction, valuePercentage, setAsValue, cpcKey = "cpc" } =
    adjustBidInfo || {};
  const expressions = [];
  const currencyCode = getAmazonCurrencyCodeFromClientData();
  const minBid = getTargetingMinBid(currencyCode);

  for (const row of selectedRows || []) {
    const expr = ensureTargetExpression(row);

    let newBid;
    if (selectedAction === "set_bid_as") {
      newBid = parseFloat(setAsValue);
    } else if (selectedAction === "copy_cpc") {
      const cpc =
        row?.[cpcKey] ?? row?.current_data?.[cpcKey] ?? row?.agg_cpc;
      newBid = isValidCpcForCopy(cpc, minBid)
        ? Number(parseFloat(cpc).toFixed(2))
        : null;
    } else {
      newBid = modifyValueByPercentage(
        selectedAction,
        valuePercentage,
        Number(row?.bid ?? row?.bid_copy ?? 0)
      );
    }

    if (newBid === null || newBid === undefined || Number.isNaN(Number(newBid))) {
      continue;
    }
    const roundedBid = roundTargetingBid(newBid, currencyCode);
    if (roundedBid === null || roundedBid < minBid) {
      continue;
    }
    expr.target[0].bid = Number(roundedBid);
    if (row?.status) {
      expr.target[0].status = String(row.status).toUpperCase();
    }
    expressions.push(expr);
  }

  return expressions;
}
